/**
 * "Dasar jawaban" — a marginal apparatus, not a search-results pane.
 *
 * It shows the sources behind the FOCUSED answer, in two kinds that are genuinely
 * different and must not be flattened into one list:
 *
 *   Dari dokumen terindeks  — real chunk citations, with pasal and page.
 *   Dari sumber data langsung — BPS / Open Data / spreadsheets. These answers come
 *   back as live-source citations. Portal citations name and link the discovered source;
 *   the call stays visible underneath as the reproducible audit record.
 *
 * Citation numbering is per CONVERSATION, not per answer. The mockups restarted at 1
 * on every turn and then ran out and switched to letters, so "[2]" meant two
 * different documents in one thread.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/lib/api.ts";
import { clsx } from "@/lib/clsx.ts";
import { citationKey } from "@/lib/api-types.ts";
import type { Citation } from "@/lib/api-types.ts";
import { EmptyState } from "./primitives.tsx";

export interface ToolEvidence {
  tool: string;
  executed: string | null;
  round?: number;
  args?: Record<string, unknown>;
  host?: string;
  fetchedAt?: string;
  rows?: number;
}

export interface EvidenceItem {
  /** Stable across the whole conversation. */
  index: number;
  citation: Citation;
  text?: string;
  /** Which corpus the passage came from (JDIH / PPID / …), from the `passage` frame. */
  sourceType?: string;
  /** Retrieval score, same source. Shown because a reader deserves to know whether a
   *  citation was a strong match or a marginal one. */
  score?: number;
}

export function EvidenceRail({
  items,
  tools,
  litIndex,
  onFocus,
  onOpenPassage,
  onOpenDocument,
}: {
  items: EvidenceItem[];
  tools: ToolEvidence[];
  litIndex: number | null;
  onFocus: (index: number | null) => void;
  /** Clicking the citation itself: show the chunk the answer leaned on. */
  onOpenPassage: (item: EvidenceItem) => void;
  /** "Buka dokumen asli": show the published file. */
  onOpenDocument: (item: EvidenceItem) => void;
}) {
  const { t } = useTranslation();
  // SPLIT BY WHAT THE SOURCE IS, not by which list it arrived in. Tool citations are new
  // (ADR-0041's ADR-0004 gap, closed) and they landed in `items` — so a live CKAN read
  // rendered under "Dari dokumen terindeks" with the tool name as its document title,
  // telling the reader that a figure fetched seconds ago was an indexed government
  // document. That inverts this component's whole contract.
  //
  // Stored rows can also carry `kind:"history"` conversation records (no document,
  // no chunk, no offsets): skip every kind that is not a document or live read
  // instead of sending it to the document card, which reads `section_path.length`.
  const indexed = items.filter((item) => item.citation.kind === "chunk");
  const live = items.filter((item) => item.citation.kind === "tool");

  return (
    <aside className="evid" aria-label={t("evidence.title")}>
      <div className="evid-head">
        <span className="eyebrow">{t("evidence.title")}</span>
      </div>
      <div className="evid-scroll">
        {items.length === 0 && tools.length === 0 ? (
          <EmptyState title={t("evidence.emptyTitle")} body={t("evidence.emptyBody")} />
        ) : null}

        {indexed.length > 0 ? (
          <>
            <div className="eyebrow" style={{ margin: "0 0 var(--s2)" }}>
              {t("evidence.fromIndexed")}
            </div>
            {indexed.map((item) => (
              <EvidenceCard
                key={citationKey(item.citation)}
                item={item}
                lit={litIndex === item.index}
                onFocus={onFocus}
                onOpenPassage={onOpenPassage}
                onOpen={onOpenDocument}
              />
            ))}
          </>
        ) : null}

        {live.length > 0 || tools.length > 0 ? (
          <>
            <div className="eyebrow" style={{ margin: "var(--s4) 0 var(--s2)" }}>
              {t("evidence.fromLive")}
            </div>
            {live.map((item) => (
              <LiveReadCard key={citationKey(item.citation)} item={item} />
            ))}
            {tools.map((tool, position) => (
              <ToolCard key={`${tool.tool}-${position}`} tool={tool} />
            ))}
          </>
        ) : null}
      </div>
    </aside>
  );
}

/**
 * A citation for something READ LIVE — a portal row, a spreadsheet figure.
 *
 * Deliberately not an `EvidenceCard`: there is no chunk to open, no document to fetch and
 * no passage text to expand, so every affordance on that card would be dead. What it has
 * instead is the CALL, which is the thing that makes the figure checkable — a reader can
 * re-run `open_data_read({...})` and see the same row.
 */
function LiveReadCard({ item }: { item: EvidenceItem }) {
  const { t } = useTranslation();
  const title = item.citation.document_title || item.citation.tool || t("evidence.fromLive");
  return (
    // `ev-*`, NOT `evid-*`. This card was written against six class names that have no
    // rule in either stylesheet — `evid-card`, `evid-card-head`, `evid-index`,
    // `evid-title`, `evid-call`, `evid-note` are declared nowhere, so every one of them
    // was dead and the card rendered unstyled: the index ran straight into the title
    // ("1Inventaris koleksi"). The `evid-` prefix DOES exist on the rail's own layout
    // (`evid-head`, `evid-scroll`, `evid-forced`), which is what made the mistake easy to
    // make and easy to miss. The CARD family is `ev-*`, as used by `EvidenceCard` below.
    <article className="ev" aria-label={item.citation.document_title}>
      <div className="ev-top">
        <span className="ev-n">{item.index}</span>
        {item.citation.source_url ? (
          <a
            className="ev-title"
            href={item.citation.source_url}
            target="_blank"
            rel="noreferrer"
          >
            {title}
          </a>
        ) : (
          <span className="ev-title">{title}</span>
        )}
      </div>
      {item.citation.call ? <code className="ev-call mono">{item.citation.call}</code> : null}
      <p className="ev-status">{t("evidence.liveReadNote")}</p>
    </article>
  );
}

function EvidenceCard({
  item,
  lit,
  onFocus,
  onOpenPassage,
  onOpen,
}: {
  item: EvidenceItem;
  lit: boolean;
  onFocus: (index: number | null) => void;
  onOpenPassage: (item: EvidenceItem) => void;
  onOpen: (item: EvidenceItem) => void;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // A REPLAYED turn carries citations but no text: `source_citations` records which
  // chunk, never its words. The card fetched nothing and said "not available", while
  // the reader fetched the same chunk and showed it — the rail looked empty beside a
  // pane full of the text it claimed not to have.
  const [fetched, setFetched] = useState<string | null>(null);
  const chunkId = item.citation.chunk_id;
  const missing = !item.text;
  useEffect(() => {
    if (!missing || !chunkId) return;
    let cancelled = false;
    void api
      .chunk(chunkId)
      .then((chunk) => {
        if (!cancelled) setFetched(chunk.text);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [missing, chunkId]);

  const passage = item.text ?? fetched ?? undefined;
  const { citation } = item;
  const location = [
    citation.legal_reference,
    citation.page_number !== null ? t("evidence.page", { page: citation.page_number }) : null,
    citation.section_path.length > 0 ? citation.section_path.join(" › ") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article
      id={`ev-${item.index}`}
      className={clsx("ev", "ev-clickable", lit && "lit")}
      // THE WHOLE CARD opens the chunk. Only the index number was clickable, so the
      // obvious target — the card you are looking at — did nothing.
      role="button"
      tabIndex={0}
      onClick={() => onOpenPassage({ ...item, text: passage })}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenPassage({ ...item, text: passage });
        }
      }}
      // Focus, not just hover: the mockups linked answer to evidence on hover alone,
      // which excluded every keyboard and touch user from the core verification move.
      onMouseEnter={() => onFocus(item.index)}
      onMouseLeave={() => onFocus(null)}
      onFocus={() => onFocus(item.index)}
      onBlur={() => onFocus(null)}
      aria-current={lit ? "true" : undefined}
    >
      <div className="ev-top">
        {/* Plain again: the CARD is the button now, and a button inside a button is
            invalid markup that screen readers report inconsistently. */}
        <span className="ev-n">{item.index}</span>
        {item.sourceType ? <span className="fam">{item.sourceType}</span> : null}
        {item.score !== undefined ? (
          <span className="ev-score mono">
            {new Intl.NumberFormat(i18n.language === "en" ? "en-GB" : "id-ID", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(item.score)}
          </span>
        ) : null}
      </div>

      {/* The title gets its own line. Sharing one with the number wrapped every real
          corpus title — which are filename-derived slugs, not "Pergub Aceh 67/2019". */}
      <div className="ev-title">{citation.document_title}</div>
      {location ? <div className="ev-loc">{location}</div> : null}

      {/* The passage is VISIBLE, not hidden behind a click. Hiding it made the card
          read as empty and turned the one thing a citation apparatus exists to show
          into something you had to go looking for. Clicking it opens the whole chunk. */}
      {passage ? (
        <p className={clsx("ev-text", expanded && "ev-text-full")}>{passage}</p>
      ) : (
        <p className="ev-text faint">{t("common.loading")}</p>
      )}

      {/* The wireframe's foot said "Berlaku" with a green tick. Nothing in this system
          knows whether a qanun is in force — only that no superseding instrument was
          found in the INDEXED corpus, which is a different claim. So the foot states
          what is actually known: the corpus it came from, and that it is indexed. */}
      <div className="ev-status">
        <span className="pill pill-green">{t("evidence.indexed")}</span>
        {item.citation.confidential ? (
          <span className="pill pill-amber">{t("collections.confidential")}</span>
        ) : null}
      </div>

      <div className="ev-foot">
        {passage && passage.length > 220 ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            // `stopPropagation`: the card itself opens the reader now, and expanding in
            // place must not also open it.
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((value) => !value);
            }}
          >
            {expanded ? t("evidence.less") : t("evidence.more")}
          </button>
        ) : null}
        <span className="spacer" />
        {/* Opens the ORIGINAL file. It 404s honestly when this environment holds the
            row but not the bytes — the local index is copied from staging's database
            while the objects stay in staging's bucket. */}
        <button
          type="button"
          className="btn btn-sm"
          onClick={(event) => {
            event.stopPropagation();
            onOpen({ ...item, text: passage });
          }}
        >
          {t("evidence.openDocument")}
        </button>
      </div>
    </article>
  );
}

function ToolCard({ tool }: { tool: ToolEvidence }) {
  const { t } = useTranslation();
  const args = Object.entries(tool.args ?? {});

  return (
    <article className="ev ev-tool">
      <div className="ev-top">
        <span className="ev-title mono">{tool.executed ?? tool.tool}</span>
      </div>
      <div className="ev-call">
        <dl>
          {args.map(([key, value]) => (
            <div className="kv" key={key}>
              <dt>{key}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
          {tool.host ? (
            <div className="kv">
              <dt>{t("evidence.toolHost")}</dt>
              <dd>{tool.host}</dd>
            </div>
          ) : null}
          {tool.rows !== undefined ? (
            <div className="kv">
              <dt>{t("evidence.toolRows")}</dt>
              <dd>{tool.rows}</dd>
            </div>
          ) : null}
          {tool.fetchedAt ? (
            <div className="kv">
              <dt>{t("evidence.toolFetched")}</dt>
              <dd>{tool.fetchedAt}</dd>
            </div>
          ) : null}
        </dl>
      </div>
      <p className="ev-text" style={{ marginTop: "var(--s2)" }}>
        {t("evidence.toolNote")}
      </p>
    </article>
  );
}
